import type { BookingRecord, ServiceType, TripStatus } from '../types'
import type { SelectOption } from '../components/ui/Select'
import { dateRanges, serviceTypes, tripStatusLabels, tripStatuses } from './bookings'

/** Sentinel for the "no filter applied" option in the service and status selects. */
export const ALL = 'all'

export type ServiceFilter = ServiceType | typeof ALL
export type StatusFilter = TripStatus | typeof ALL

export type BookingsFilters = {
  query: string;
  service: ServiceFilter;
  quickFilter: string;

  // sourceNumber: string;
  sourceName: string;

  rangeId: string;
};

export const defaultFilters: BookingsFilters = {
  query: '',
  service: ALL,
  quickFilter: ALL,

  // sourceNumber: '',
  sourceName: '',
  rangeId: ALL,
};


export const serviceOptions: SelectOption<ServiceFilter>[] = [
  { value: ALL, label: 'All Services' },
  ...serviceTypes.map((service) => ({ value: service, label: service })),
]

export const statusOptions: SelectOption<StatusFilter>[] = [
  { value: ALL, label: 'All Status' },
  ...tripStatuses.map((status) => ({ value: status, label: tripStatusLabels[status] })),
]

export const rangeOptions: SelectOption<string>[] = [
  { value: ALL, label: 'All Dates' },
  { value: 'today', label: 'Today' },
  { value: 'this-week', label: 'This Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-30', label: 'Last 30 Days' },
  ...dateRanges.map((range) => ({
    value: range.id,
    label: range.label,
  })),
]

/** Checks if scheduledAt matches the chosen dynamic preset or custom date range */
const withinRange = (scheduledAt: string, rangeId: string) => {
  if (!rangeId || rangeId === ALL) return true

  const day = scheduledAt.slice(0, 10)

  if (rangeId === 'today') {
    const today = new Date().toISOString().slice(0, 10)
    return day === today
  }

  if (rangeId === 'this-week') {
    const now = new Date()
    const startOfWeek = new Date(now)
    startOfWeek.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1))
    const endOfWeek = new Date(startOfWeek)
    endOfWeek.setDate(startOfWeek.getDate() + 6)
    const from = startOfWeek.toISOString().slice(0, 10)
    const to = endOfWeek.toISOString().slice(0, 10)
    return day >= from && day <= to
  }

  if (rangeId === 'this-month') {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)
    return day >= from && day <= to
  }

  if (rangeId === 'last-30') {
    const now = new Date()
    const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    const to = now.toISOString().slice(0, 10)
    return day >= from && day <= to
  }

  if (rangeId.startsWith('custom:')) {
    const [from, to] = rangeId.replace('custom:', '').split('_')
    if (from && to) return day >= from && day <= to
    if (from) return day >= from
  }

  const range = dateRanges.find((candidate) => candidate.id === rangeId)
  if (!range) return true

  return day >= range.from && day <= range.to
}

export const filterBookings = (bookings: BookingRecord[], filters: BookingsFilters) => {
  const query = filters.query.trim().toLowerCase()

  return bookings.filter((booking) => {
    if (query && !booking.reference.toLowerCase().includes(query)) return false
    if (filters.service !== ALL && booking.service !== filters.service) return false

    // if (
    //   filters.sourceNumber &&
    //   !booking.sourceNumber?.toLowerCase().includes(filters.sourceNumber.toLowerCase())
    // ) {
    //   return false
    // }

    if (
      filters.sourceName &&
      !booking.sourceName?.toLowerCase().includes(filters.sourceName.toLowerCase())
    ) {
      return false
    }

    // Quick filter logic
    if (filters.quickFilter !== ALL) {
      switch (filters.quickFilter) {
        case 'Upcoming':
          if (booking.trip !== 'upcoming') return false;
          break;
        case 'Completed Today': {
          if (booking.trip !== 'completed') return false;
          const today = new Date().toISOString().slice(0, 10);
          if (booking.scheduledAt.slice(0, 10) !== today) return false;
          break;
        }
        case 'Pending Payment':
          if (booking.payment !== 'unpaid') return false;
          break;
        case 'Unassigned Driver':
          if (booking.driverAssigned !== false) return false;
          break;
        case 'Cancelled':
          if (booking.trip !== 'cancelled') return false;
          break;
        case 'High Value (> €500)':
          if (booking.amountEur <= 500) return false;
          break;
      }
    }

    return withinRange(booking.scheduledAt, filters.rangeId)
  })
}
